import React, { useEffect, useState } from 'react';
import Navbar from './Navbar';
import Leads from './leads';
import EmailTemplates from './EmailTemplates';
import Salespeople from './salespeople';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const temperatureColor = {
    cold: 'bg-sky-300',
    warm: 'bg-orange-300',
    hot: 'bg-red-300',
};

function getStartOfDay(date = new Date()) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
function getStartOfYesterday() {
    const d = getStartOfDay();
    d.setDate(d.getDate() - 1);
    return d;
}
function getStartOfWeek() {
    const d = getStartOfDay();
    d.setDate(d.getDate() - d.getDay());
    return d;
}
function getStartOfMonth() {
    const d = getStartOfDay();
    d.setDate(1);
    return d;
}
function getStartOfYear() {
    const d = getStartOfDay();
    d.setMonth(0, 1);
    return d;
}
function inRange(ts, start, end) {
    return ts >= start.getTime() && ts < end.getTime();
}

function getSaleSiteKey(sale) {
    const site = String(sale?.Site ?? sale?.site ?? '').trim().toLowerCase();
    return site === 'avenor' ? 'avenor' : 'normal';
}

function getSaleSiteLabel(siteKey) {
    return siteKey === 'avenor' ? 'Avenor' : 'InsightOS';
}

const TABS = [
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
];

function getPeriodRange(tab) {
    const now = new Date();
    if (tab === 'day') {
        const start = getStartOfDay(now);
        const end = new Date(start);
        end.setDate(start.getDate() + 1);
        const prevStart = getStartOfYesterday();
        const prevEnd = start;
        return { start, end, prevStart, prevEnd };
    }
    if (tab === 'week') {
        const start = getStartOfWeek(now);
        const end = new Date(start);
        end.setDate(start.getDate() + 7);
        const prevStart = new Date(start);
        prevStart.setDate(start.getDate() - 7);
        const prevEnd = start;
        return { start, end, prevStart, prevEnd };
    }
    if (tab === 'month') {
        const start = getStartOfMonth(now);
        const end = new Date(start);
        end.setMonth(start.getMonth() + 1);
        const prevStart = new Date(start);
        prevStart.setMonth(start.getMonth() - 1);
        const prevEnd = start;
        return { start, end, prevStart, prevEnd };
    }
    if (tab === 'year') {
        const start = getStartOfYear(now);
        const end = new Date(start);
        end.setFullYear(start.getFullYear() + 1);
        const prevStart = new Date(start);
        prevStart.setFullYear(start.getFullYear() - 1);
        const prevEnd = start;
        return { start, end, prevStart, prevEnd };
    }
    return {};
}

export default function SalesTable() {
    const [sales, setSales] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [emailModal, setEmailModal] = useState({ open: false, sale: null });
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [aiAnalysis, setAiAnalysis] = useState('');
    const [stats, setStats] = useState([]);
    const [siteStats, setSiteStats] = useState({ avenor: [], normal: [] });
    const [tab, setTab] = useState('day');
    const [activeSection, setActiveSection] = useState('sales');

    useEffect(() => {
        fetchSales();
        const interval = setInterval(() => {
            fetchSales();
        }, 60000); // 1 minute
        return () => clearInterval(interval);
    }, []);

    const fetchSales = async () => {
        try {
            const response = await fetch('https://api.insightos.co.uk/admin/sales', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            const data = await response.json();
            setSales(data.data || []);
        } catch (error) {
            console.error('Error fetching sales data:', error);
        }
    };

    const toggleExpand = (id) => {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const openEmailModal = (sale) => {
        setEmailModal({ open: true, sale });
        setEmailSubject('');
        setEmailBody('');
        setAiAnalysis('');
    };

    const closeEmailModal = () => {
        setEmailModal({ open: false, sale: null });
        setEmailSubject('');
        setEmailBody('');
        setAiAnalysis('');
    };

    const handleSendEmail = () => {
        alert('Email sent!');
        closeEmailModal();
    };

    const handleAIAnalyse = async () => {
        setAiAnalysis('AI Analysis: This email is clear and actionable.');
    };

    // Helper to get the last temperature from salesBot
    const getCurrentTemperature = (sale) => {
        if (!sale.salesBot || sale.salesBot.length === 0) return null;
        const lastBot = sale.salesBot[sale.salesBot.length - 1];
        return (lastBot.Temperature || '').toLowerCase();
    };

    // Stats calculation
    useEffect(() => {
        if (!sales.length) {
            setStats([]);
            setSiteStats({ avenor: [], normal: [] });
            return;
        }

        const { start, end, prevStart, prevEnd } = getPeriodRange(tab);

        const salesBySite = {
            avenor: sales.filter(sale => getSaleSiteKey(sale) === 'avenor'),
            normal: sales.filter(sale => getSaleSiteKey(sale) === 'normal'),
        };

        const buildStats = (siteSales) => {
            if (!siteSales.length) {
                return [];
            }

            // Visits
            let visits = 0, prevVisits = 0;
            siteSales.forEach(sale => {
                const created = sale.createdAt ? new Date(sale.createdAt).getTime() : 0;
                if (inRange(created, start, end)) visits += sale.Visits || 0;
                if (inRange(created, prevStart, prevEnd)) prevVisits += sale.Visits || 0;
            });
            const visitsChange = prevVisits === 0 ? 100 : ((visits - prevVisits) / prevVisits * 100).toFixed(1);

            // This period filter
            const periodSales = siteSales.filter(sale => {
                const created = sale.createdAt ? new Date(sale.createdAt).getTime() : 0;
                return inRange(created, start, end);
            });

            // Sales (Converted) this period
            const salesConverted = periodSales.filter(sale => sale.Converted).length;
            // PriceClick this period
            const priceClick = periodSales.filter(sale => sale.PriceClick).length;
            // TrialClick this period
            const trialClick = periodSales.filter(sale => sale.TrialClick).length;
            // TermsClick this period
            const termsClick = periodSales.filter(sale => sale.TermsClick).length;

            // Cold/Warm/Hot leads this period (not converted)
            let cold = 0, warm = 0, hot = 0;
            periodSales.forEach(sale => {
                if (sale.Converted) return;
                if (!sale.salesBot || !sale.salesBot.length) return;
                const lastBot = sale.salesBot[sale.salesBot.length - 1];
                const temp = (lastBot.Temperature || '').toLowerCase();
                if (temp === 'cold') cold++;
                else if (temp === 'warm') warm++;
                else if (temp === 'hot') hot++;
            });

            // Live Users (Live === 1)
            const liveUsers = siteSales.filter(sale => sale.Live === 1).length;

            // Average Duration (in minutes)
            const totalDuration = siteSales.reduce((sum, sale) => sum + (sale.Duration || 0), 0);
            const averageDuration = totalDuration > 0 ? (totalDuration / siteSales.length).toFixed(1) : 0;

            return [
                {
                    name: 'Live Users',
                    stat: liveUsers,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Inbound Visits',
                    stat: visits,
                    previousStat: prevVisits,
                    change: `${Math.abs(visitsChange)}%`,
                    changeType: visitsChange >= 0 ? 'increase' : 'decrease',
                },
                {
                    name: 'Inbound Sales',
                    stat: salesConverted,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Inbound Price Clicks',
                    stat: priceClick,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Inbound Trial Clicks',
                    stat: trialClick,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Inbound Terms Clicks',
                    stat: termsClick,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Inbound Cold Leads',
                    stat: cold,
                    previousStat: '-',
                    change: '',
                    changeType: 'decrease',
                },
                {
                    name: 'Inbound Warm Leads',
                    stat: warm,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Inbound Hot Leads',
                    stat: hot,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
                {
                    name: 'Average Duration (min)',
                    stat: averageDuration,
                    previousStat: '-',
                    change: '',
                    changeType: 'increase',
                },
            ];
        };

        setSiteStats({
            avenor: buildStats(salesBySite.avenor),
            normal: buildStats(salesBySite.normal),
        });

        setStats(buildStats(sales));
    }, [sales, tab]);

    const salesBySite = {
        avenor: sales.filter(sale => getSaleSiteKey(sale) === 'avenor'),
        normal: sales.filter(sale => getSaleSiteKey(sale) === 'normal'),
    };

    const renderStatsSection = (siteKey, title, sectionStats) => (
        <div className="px-4 sm:px-6 lg:px-8 mt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                    <p className="mt-1 text-sm text-gray-600">
                        {siteKey === 'avenor'
                            ? 'Metrics filtered to Avenor sales only.'
                            : 'Metrics filtered to sales where Site is null.'}
                    </p>
                </div>
                <span className={classNames(
                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                    siteKey === 'avenor' ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'
                )}>
                    {getSaleSiteLabel(siteKey)}
                </span>
            </div>
            <div className="flex gap-2 mb-2 mt-5">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        className={classNames(
                            'px-4 py-2 rounded font-semibold text-sm',
                            tab === t.key
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{title} ({TABS.find(t => t.key === tab)?.label})</h3>
            <dl className="mt-2 grid grid-cols-1 divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-3 md:divide-x md:divide-y-0">
                {(sectionStats || []).map((item) => (
                    <div key={item.name} className="px-4 py-5 sm:p-6">
                        <dt className="text-base font-normal text-gray-900">{item.name}</dt>
                        <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                            <div className="flex items-baseline text-2xl font-semibold text-indigo-600">
                                {item.stat}
                                {item.previousStat !== '-' && (
                                    <span className="ml-2 text-sm font-medium text-gray-500">from {item.previousStat}</span>
                                )}
                            </div>
                            {item.change && (
                                <div
                                    className={classNames(
                                        item.changeType === 'increase' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
                                        'inline-flex items-baseline rounded-full px-2.5 py-0.5 text-sm font-medium md:mt-2 lg:mt-0',
                                    )}
                                >
                                    {item.changeType === 'increase' ? (
                                        <ArrowUpIcon aria-hidden="true" className="-ml-1 mr-0.5 size-5 shrink-0 self-center text-green-500" />
                                    ) : (
                                        <ArrowDownIcon aria-hidden="true" className="-ml-1 mr-0.5 size-5 shrink-0 self-center text-red-500" />
                                    )}
                                    <span className="sr-only"> {item.changeType === 'increase' ? 'Increased' : 'Decreased'} by </span>
                                    {item.change}
                                </div>
                            )}
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );

    const renderSalesTable = (siteKey, title, sectionSales) => (
        <div className="px-4 sm:px-6 lg:px-8 mt-6">
            <div className="sm:flex sm:items-center mb-6">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                    <p className="mt-1 text-md text-gray-600">
                        {siteKey === 'avenor'
                            ? 'Avenor sales records, including company, contact, engagement, and conversion details.'
                            : 'Sales records where Site is null, including company, contact, engagement, and conversion details.'}
                    </p>
                </div>
            </div>
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className={siteKey === 'avenor' ? 'bg-amber-50' : 'bg-gray-50'}>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Company</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Industry</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Visits</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Price Click</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Terms Click</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Trial Click</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Converted</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Score</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Current Temperature</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {sectionSales.map((sale, idx) => {
                                const temp = getCurrentTemperature(sale);
                                const rowClass = classNames(
                                    sale.Live === 1 ? 'bg-emerald-200' : '',
                                    siteKey === 'avenor' && 'bg-amber-50/40',
                                    'hover:bg-gray-50 transition'
                                );
                                return (
                                    <React.Fragment key={sale.id || idx}>
                                        <tr className={rowClass}>
                                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{sale.CompanyName}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{sale.Email}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{sale.Industry}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{sale.Location}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{sale.Visits}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={sale.PriceClick ? "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" : "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"}>
                                                    {sale.PriceClick ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={sale.TermsClick ? "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" : "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"}>
                                                    {sale.TermsClick ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={sale.TrialClick ? "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" : "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"}>
                                                    {sale.TrialClick ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={sale.Converted ? "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" : "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"}>
                                                    {sale.Converted ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{sale.Score}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">{sale.createdAt ? new Date(sale.createdAt).toLocaleString() : ''}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {temp ? (
                                                    <span className={classNames(
                                                        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-gray-900 capitalize",
                                                        temperatureColor[temp] || "bg-gray-200"
                                                    )}>
                                                        {temp}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                                        N/A
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right flex flex-col gap-2">
                                                <button
                                                    className="inline-flex items-center px-3 py-1.5 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 transition text-xs font-semibold"
                                                    onClick={() => toggleExpand(sale.id)}
                                                >
                                                    {expanded[sale.id] ? 'Hide Bot' : 'Show Bot'}
                                                </button>
                                                <button
                                                    className="inline-flex items-center px-3 py-1.5 border border-green-600 text-green-600 rounded hover:bg-green-50 transition text-xs font-semibold"
                                                    onClick={() => openEmailModal(sale)}
                                                >
                                                    Send Email
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded[sale.id] && sale.salesBot && sale.salesBot.length > 0 && (
                                            <tr>
                                                <td colSpan={12} className="bg-gray-50 px-8 py-6">
                                                    <div className="overflow-x-auto">
                                                        <div className="grid grid-cols-5 gap-6 font-semibold border-b pb-2 mb-2 text-gray-700">
                                                            <div>Question</div>
                                                            <div>AI Answer</div>
                                                            <div>Buying Signal</div>
                                                            <div>Follow Up</div>
                                                            <div>Temperature</div>
                                                        </div>
                                                        {sale.salesBot.map((bot, i) => (
                                                            <div
                                                                key={bot.id || i}
                                                                className={classNames(
                                                                    'grid grid-cols-5 gap-6 items-start border-b py-2 text-sm',
                                                                    temperatureColor[(bot.Temperature || '').toLowerCase()] || ''
                                                                )}
                                                            >
                                                                <div className="break-words">{bot.Question}</div>
                                                                <div className="break-words">{bot.AIAnswer}</div>
                                                                <div className="break-words">{bot.BuyingSignal}</div>
                                                                <div className="break-words">{bot.FollowUpQuestion}</div>
                                                                <div className="capitalize">{bot.Temperature}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {sectionSales.length === 0 && (
                                <tr>
                                    <td colSpan={12} className="text-center py-12 text-gray-400 text-lg font-medium">
                                        No sales records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );



    return (
        <>
            <Navbar />
            <div className="px-4 sm:px-6 lg:px-8 mt-4">
                <div className="flex flex-wrap gap-3">
                    <button
                        className={classNames(
                            'px-4 py-2 rounded font-semibold text-sm',
                            activeSection === 'sales'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                        onClick={() => setActiveSection('sales')}
                    >
                        Sales Dashboard
                    </button>
                    <button
                        className={classNames(
                            'px-4 py-2 rounded font-semibold text-sm',
                            activeSection === 'leads'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                        onClick={() => setActiveSection('leads')}
                    >
                        Lead Gen
                    </button>
                    <button
                        className={classNames(
                            'px-4 py-2 rounded font-semibold text-sm',
                            activeSection === 'emailTemplates'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                        onClick={() => setActiveSection('emailTemplates')}
                    >
                        Email Templates
                    </button>
                    <button
                        className={classNames(
                            'px-4 py-2 rounded font-semibold text-sm',
                            activeSection === 'salespeople'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                        onClick={() => setActiveSection('salespeople')}
                    >
                        Salespeople
                    </button>
                </div>
            </div>

            {activeSection === 'leads' ? (
                <div className="px-4 sm:px-6 lg:px-8 mt-6">
                    <Leads />
                </div>
            ) : activeSection === 'emailTemplates' ? (
                <div className="px-4 sm:px-6 lg:px-8 mt-6">
                    <EmailTemplates />
                </div>
            ) : activeSection === 'salespeople' ? (
                <div className="px-4 sm:px-6 lg:px-8 mt-6">
                    <Salespeople />
                </div>
            ) : (
                <>
                    {renderStatsSection('avenor', 'Avenor Sales Stats', siteStats.avenor)}
                    {renderStatsSection('normal', 'InsightOS Sales Stats', siteStats.normal)}

                    {renderSalesTable('avenor', 'Avenor Sales Dashboard', salesBySite.avenor)}
                    {renderSalesTable('normal', 'InsightOS Sales Dashboard', salesBySite.normal)}
                </>
            )}

            {/* Email Modal */}
            {emailModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-xl relative">
                        <button
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            onClick={closeEmailModal}
                            aria-label="Close"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <h2 className="text-xl font-bold mb-6 text-gray-900">Send Email</h2>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                            <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-700"
                                value={emailModal.sale?.Email}
                                disabled
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                value={emailSubject}
                                onChange={e => setEmailSubject(e.target.value)}
                                placeholder="Enter subject"
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                            <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                rows={6}
                                value={emailBody}
                                onChange={e => setEmailBody(e.target.value)}
                                placeholder="Write your message..."
                            />
                        </div>
                        <div className="flex gap-3 mb-4">
                            <button
                                className="bg-indigo-600 text-white px-5 py-2 rounded font-semibold hover:bg-indigo-700 transition"
                                onClick={handleSendEmail}
                            >
                                Send
                            </button>
                            <button
                                className="bg-gray-200 text-gray-800 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition"
                                onClick={closeEmailModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-blue-600 text-white px-5 py-2 rounded font-semibold hover:bg-blue-700 transition"
                                onClick={handleAIAnalyse}
                            >
                                AI Analyse
                            </button>
                        </div>
                        {aiAnalysis && (
                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                                {aiAnalysis}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
